import React, { useMemo } from "react";
import PropTypes from "prop-types";
import {
    DmsModal,
    DmsButton,
    DmsPermissions,
    havePermissions,
    intl,
    DmsTextField,
    DmsTextFieldCnpj,
    DmsTextFieldEmail,
    DmsTextFieldPhone,
    Notifications
} from "dmsbasereact";
import {
    Box,
    useMediaQuery,
    useTheme,
    Grid,
    Typography,
    CircularProgress,
    Chip,
    Autocomplete,
    TextField,
    IconButton,
    Tooltip,
    Divider,
    Checkbox,
    FormControlLabel,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SendIcon from "@mui/icons-material/Send";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Permissions } from "../../constants/Permissions";
import { InstitutionStatusEnum } from "../../constants/InstitutionStatus";

import { DmsAutocomplete } from "../DmsAutoComplete/DmsAutoComplete";
import { DocumentUploadSlotWithExpiration } from "./DocumentUploadSlotWithExpiration";
import { useInstitutionSetupForm } from "./useInstitutionSetupForm";
import { EXCLUSIVE_CATEGORY } from "../../constants/InstitutionType";
import { getBrazilianStateOptions } from "../../constants/BrazilianStates";
import { documentRequiresExpiration } from "../../constants/DocumentRequirements";
import { DmsTextFieldCep } from "../DmsTextFieldCep";

/**
 * Modal component for creating/editing institutions.
 * Supports view, edit, and create modes.
 */
const InstitutionSetupModal = ({
    isVisible,
    onClose,
    onSave,
    onRefresh = null,
    onEditClick = null,
    institution = null,
    mode,
    showEditButton = false,
    publicMode = false, // Public mode for registration (no auth, simplified form)
    linkToUser = false, // If true, auto-link created institution to current user
    existingInstitutionPks = [], // Existing institution pks to preserve when auto-linking
    userType = null, // User type for category filtering (from USER_TYPE_VALUES)
}) => {
    const {
        isViewMode,
        isEditMode,
        formData,
        handleFormChange,
        files,
        handleFileSelect,
        handleExemptionChange,
        requiredDocs,
        handleSubmit,
        // Multi-type support
        institutionTypes,
        handleInstitutionTypeChange,
        availableCategoryOptions,
        categoryError,
        title,
        saving,
        isFormValid,
        fieldErrors,
        // Category request state (for submit functionality)
        categoryRequests,
        submittingCategoryRequest,
        handleSubmitForApproval,
        // User type based category locking
        isCategoryFieldLocked,
    } = useInstitutionSetupForm({ isVisible, onSave, institution, mode, publicMode, linkToUser, existingInstitutionPks, userType });

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    // Check if user has Eurofarma permissions (can edit institution type and partner groups)
    const hasEurofarmaPermissions = havePermissions([
        Permissions.EUROFARMA_MANAGEMENT,
        Permissions.EUROFARMA_REGULATORY,
        Permissions.EUROFARMA_GESTAO_LENA
    ], false);

    const stateOptions = getBrazilianStateOptions().map(({ value, label }) => ({
        value,
        label,
    }));

    // Institution types info tooltip content
    const institutionTypesInfoTooltip = (
        <Box sx={{ p: 1, maxWidth: 400 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                {intl.get("InstitutionTypes.Info.Title").d("Institution Types")}
            </Typography>

            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("Distributor").d("Distributor")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("InstitutionTypes.Info.Distributor").d(
                        "Entity that purchases directly from Eurofarma and sells the medication exclusively to clients previously approved by the Lena Program."
                    )}
                </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("Dispenser").d("Dispenser")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("InstitutionTypes.Info.Dispenser").d(
                        "Entity with linked pharmacists that dispenses medication to patients."
                    )}
                </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("Intermediary").d("Intermediary")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("InstitutionTypes.Info.Intermediary").d(
                        "Entity that receives the medication and transfers units to the same economic group."
                    )}
                </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box sx={{ mb: 1.5 }}>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("Prescriber").d("Prescriber")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("InstitutionTypes.Info.Prescriber").d(
                        "Entity with linked physicians that issue prescriptions."
                    )}
                </Typography>
            </Box>

            <Divider sx={{ my: 1 }} />

            <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: "primary.main" }}>
                    {intl.get("SalesChannel").d("Sales Channel")}:
                </Typography>
                <Typography variant="body2" sx={{ fontSize: "0.8rem" }}>
                    {intl.get("InstitutionTypes.Info.SalesChannel").d(
                        "Customer that makes direct purchases from Eurofarma."
                    )}
                </Typography>
            </Box>
        </Box>
    );

    // Check what can be submitted:
    // 1. New institution in PENDING_DOCUMENT_UPLOAD status (institution.status = 8)
    // 2. Category request in PENDING_DOCUMENT_UPLOAD status (for approved institutions adding categories)

    // Check if institution itself is pending document upload (new buyer institution)
    const isInstitutionPendingDocuments = institution?.status === InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD;

    // Check if there's a category request pending (approved institution adding category)
    const pendingCategoryRequest = categoryRequests.find(
        req => req.status === InstitutionStatusEnum.PENDING_DOCUMENT_UPLOAD && req.category === 104
    );

    // Show submit button if either condition is true
    const showSubmitButton = !publicMode && mode === 'edit' && (isInstitutionPendingDocuments || pendingCategoryRequest);

    // Check if at least 1 document is uploaded (required for submission)
    const hasAtLeastOneDocument = useMemo(() => {
        // Check all documents in the files state
        for (const docName in files) {
            const docData = files[docName];
            // Document is uploaded if has new file OR existing file
            const isUploaded = !!(docData?.file || docData?.existingFile);
            if (isUploaded) {
                return true;
            }
        }
        return false;
    }, [files]);

    // Determine submission type and handle submit
    const handleSubmitClick = async () => {
        // First, save any pending changes
        const saveResult = await handleSubmit();

        // Check if save was successful
        if (!saveResult || !saveResult.success) {
            Notifications.toast(
                saveResult?.error || intl.get('ErrorSavingInstitution').d('Error saving institution'),
                Notifications.TYPE_ERROR
            );
            return;
        }

        // Then submit for approval
        let result;

        if (isInstitutionPendingDocuments) {
            // Submit new institution
            result = await handleSubmitForApproval('institution', null);
        } else if (pendingCategoryRequest) {
            // Submit category request
            result = await handleSubmitForApproval('category_request', pendingCategoryRequest.pk);
        } else {
            return;
        }

        if (result.success) {
            Notifications.toast(
                intl.get('CategoryRequestSubmitted').d('Submitted for approval successfully'),
                Notifications.TYPE_SUCCESS
            );
            // Refresh tables to show updated status
            if (onRefresh) {
                onRefresh();
            }
            // Close modal after successful submission
            onClose();
        } else {
            Notifications.toast(
                result.error || intl.get('ErrorSubmitting').d('Error submitting for approval'),
                Notifications.TYPE_ERROR
            );
        }
    };

    return (
        <DmsModal
            isVisible={isVisible}
            onClose={onClose}
            fullScreen={isMobile}
            maxWidth="md"
            fullWidth
        >
            <DmsModal.Header title={title} />
            <DmsModal.Body>
                <Box sx={{ p: isMobile ? 1 : 2 }}>
                    {/* Institution Data Section */}
                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: "text.secondary" }}>
                        {intl.get("InstitutionData").d("Institution Data")}
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <DmsTextField
                                value={formData.institution_name}
                                onChange={(e) => handleFormChange('institution_name', e.target.value)}
                                name="institution_name"
                                label={intl.get("Name").d("Name")}
                                fullWidth
                                disabled={isViewMode}
                                required
                                error={!!fieldErrors.institution_name}
                                helperText={fieldErrors.institution_name}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={formData.is_public}
                                        onChange={(e) => handleFormChange('is_public', e.target.checked)}
                                        disabled={isViewMode}
                                    />
                                }
                                label={intl.get("IsPublicInstitution").d("Is Public Institution")}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextFieldCnpj
                                name="cnpj"
                                label={intl.get("CNPJ").d("CNPJ")}
                                fullWidth
                                value={formData.cnpj}
                                onChange={(e) => handleFormChange('cnpj', e.target.value)}
                                disabled={isViewMode}
                                required={!formData.is_public}
                                error={!!fieldErrors.cnpj}
                                helperText={fieldErrors.cnpj}
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextFieldEmail
                                name='institution_email'
                                fullWidth
                                value={formData.institution_email}
                                onChange={(e) => handleFormChange('institution_email', e.target.value)}
                                disabled={isViewMode}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextFieldPhone
                                name="institution_phone"
                                label={intl.get("Phone").d("Phone")}
                                fullWidth
                                value={formData.institution_phone}
                                onChange={(e) => handleFormChange('institution_phone', e.target.value)}
                                disabled={isViewMode}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
                                <Box sx={{ flex: 1 }}>
                                    <Autocomplete
                                        multiple
                                        options={availableCategoryOptions}
                                        getOptionLabel={(option) => option.label}
                                        value={availableCategoryOptions.filter(opt => institutionTypes.includes(opt.value))}
                                        onChange={(event, newValue) => {
                                            handleInstitutionTypeChange(newValue.map(v => v.value));
                                        }}
                                        isOptionEqualToValue={(option, value) => option.value === value.value}
                                        disabled={isViewMode || (isEditMode && !hasEurofarmaPermissions) || isCategoryFieldLocked}
                                        renderTags={(value, getTagProps) =>
                                            value.map((option, index) => (
                                                <Chip
                                                    label={option.label}
                                                    size="small"
                                                    color={option.value === EXCLUSIVE_CATEGORY ? "warning" : "primary"}
                                                    {...getTagProps({ index })}
                                                    key={option.value}
                                                />
                                            ))
                                        }
                                        renderInput={(params) => (
                                            <TextField
                                                {...params}
                                                label={intl.get('InstitutionType').d('Institution Type')}
                                                required
                                                error={!!categoryError || !!fieldErrors.institutionTypes}
                                                helperText={fieldErrors.institutionTypes || categoryError || intl.get('SelectOneOrMoreTypes').d('Select one or more types (Distributor is exclusive)')}
                                            />
                                        )}
                                    />
                                </Box>
                                <Tooltip
                                    title={institutionTypesInfoTooltip}
                                    arrow
                                    placement="right"
                                    componentsProps={{
                                        tooltip: {
                                            sx: {
                                                bgcolor: "background.paper",
                                                color: "text.primary",
                                                boxShadow: 3,
                                                border: "1px solid",
                                                borderColor: "divider",
                                                "& .MuiTooltip-arrow": {
                                                    color: "background.paper",
                                                    "&::before": {
                                                        border: "1px solid",
                                                        borderColor: "divider",
                                                    }
                                                }
                                            }
                                        }
                                    }}
                                >
                                    <IconButton
                                        size="small"
                                        sx={{
                                            mt: 1,
                                            color: "info.main",
                                            "&:hover": {
                                                color: "info.dark",
                                                backgroundColor: "transparent"
                                            }
                                        }}
                                    >
                                        <InfoOutlinedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextFieldCep
                                name="institution_zip_code"
                                label={intl.get("CEP").d("Zip Code")}
                                value={formData.institution_zip_code}
                                onChange={(e) => handleFormChange('institution_zip_code', e.target.value)}
                                fullWidth
                                disabled={isViewMode}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextField
                                name="institution_city"
                                label={intl.get("City").d("City")}
                                value={formData.institution_city}
                                onChange={(e) => handleFormChange('institution_city', e.target.value)}
                                fullWidth
                                disabled={isViewMode}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsAutocomplete
                                options={stateOptions}
                                label={intl.get('State').d('State')}
                                name="institution_state"
                                value={formData.institution_state}
                                onChange={(value) => handleFormChange('institution_state', value)}
                                fullWidth
                                disabled={isViewMode}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextField
                                name="institution_address"
                                label={intl.get("Address").d("Address")}
                                fullWidth
                                value={formData.institution_address}
                                onChange={(e) => handleFormChange('institution_address', e.target.value)}
                                disabled={isViewMode}
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DmsTextField
                                name="institution_number"
                                label={intl.get("Number").d("Number")}
                                value={formData.institution_number}
                                onChange={(e) => handleFormChange('institution_number', e.target.value)}
                                fullWidth
                                disabled={isViewMode}
                                required
                            />
                        </Grid>
                    </Grid>

                    {/* Documents Section - Hidden in public mode (documents can be uploaded later) */}
                    {!publicMode && requiredDocs.length > 0 && (
                        <>
                            <Typography variant="subtitle2" sx={{ mt: 3, mb: 1, fontWeight: 600, color: "text.secondary" }}>
                                {intl.get("Documents").d("Documents")}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                {intl.get("DocumentsOptionalInfo").d("Documents are optional at this stage. You can add them later before submitting for approval.")}
                            </Typography>
                            <Grid container spacing={2}>
                                {requiredDocs.map(doc => (
                                    <Grid item xs={12} sm={6} key={doc}>
                                        <DocumentUploadSlotWithExpiration
                                            docName={doc}
                                            file={files[doc]?.file}
                                            expirationDate={files[doc]?.expirationDate}
                                            existingFile={files[doc]?.existingFile}
                                            onFileSelect={handleFileSelect}
                                            requiresExpiration={documentRequiresExpiration(doc)}
                                            disabled={isViewMode}
                                            error={!!fieldErrors[`doc_${doc}`]}
                                            errorMessage={fieldErrors[`doc_${doc}`]}
                                            allowRemove={!isEditMode || !files[doc]?.existingFile}
                                            isExempt={files[doc]?.isExempt || false}
                                            exemptionNote={files[doc]?.exemptionNote || ''}
                                            onExemptionChange={handleExemptionChange}
                                        />
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Box>
            </DmsModal.Body>
            <DmsModal.Footer>
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1,
                        width: "100%",
                        p: isMobile ? 1 : 2,
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: isMobile ? "column" : "row",
                            justifyContent: "flex-end",
                            gap: 1,
                        }}
                    >
                        {!isViewMode ? (
                            <>
                                <DmsButton
                                    variant="secondary"
                                    text={intl.get("Cancel").d("Cancel")}
                                    onClick={onClose}
                                    fullWidth={isMobile}
                                    disabled={saving || submittingCategoryRequest}
                                />
                                <DmsButton
                                    variant="primary"
                                    text={saving ? "" : intl.get("Save").d("Save")}
                                    onClick={handleSubmit}
                                    fullWidth={isMobile}
                                    disabled={saving || !isFormValid || submittingCategoryRequest}
                                    icon={saving ? <CircularProgress size={20} color="inherit" /> : null}
                                />
                                {showSubmitButton && (
                                    <DmsPermissions permissions={Permissions.INSTITUTION_MANAGEMENT_EDIT}>
                                        <DmsButton
                                            variant="success"
                                            text={submittingCategoryRequest ?
                                                intl.get('Submitting').d('Submitting...') :
                                                intl.get('SubmitForApproval').d('Submit for Approval')
                                            }
                                            onClick={handleSubmitClick}
                                            fullWidth={isMobile}
                                            disabled={submittingCategoryRequest || saving || !isFormValid || !hasAtLeastOneDocument}
                                            icon={submittingCategoryRequest ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                        />
                                    </DmsPermissions>
                                )}
                            </>
                        ) : (
                            <>
                                <DmsButton
                                    variant="secondary"
                                    text={intl.get("Close").d("Close")}
                                    onClick={onClose}
                                    fullWidth={isMobile}
                                />
                                {showEditButton && onEditClick && (
                                    <DmsPermissions permissions={Permissions.INSTITUTION_MANAGEMENT_EDIT}>
                                        <DmsButton
                                            variant="primary"
                                            text={intl.get("Edit").d("Edit")}
                                            icon={<EditIcon />}
                                            onClick={onEditClick}
                                            fullWidth={isMobile}
                                        />
                                    </DmsPermissions>
                                )}
                            </>
                        )}
                    </Box>
                </Box>
            </DmsModal.Footer>
        </DmsModal>
    );
};

InstitutionSetupModal.propTypes = {
    isVisible: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onRefresh: PropTypes.func,
    onEditClick: PropTypes.func,
    institution: PropTypes.object,
    mode: PropTypes.oneOf(['view', 'edit', 'create']).isRequired,
    showEditButton: PropTypes.bool,
    publicMode: PropTypes.bool,
    linkToUser: PropTypes.bool,
    existingInstitutionPks: PropTypes.arrayOf(PropTypes.number),
    userType: PropTypes.string, // User type from USER_TYPE_VALUES for category filtering
};


export { InstitutionSetupModal };
